var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var _a, _b, _c, _d, _e;
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { RECIPES_LIST, RECIPES_LIST_CONTENT, RECIPES_LIST_CARD, RECIPES_LIST_FOOTER, RECIPES_LIST_END_REACHED, RECIPES_LIST_LOAD_MORE, } from '../../constants/class-names';
import { MEDIUM_CLASS, LARGE_CLASS, EXTRA_LARGE_CLASS, useElementWidth, useClassNames, MEDIUM_SIZE, LARGE_SIZE, EXTRA_LARGE_SIZE, } from '../../util/screen';
import { RecipeCardSkeleton } from '../recipe-card/skeleton';
var RecipesListContainer = styled.div({
    display: 'flex',
    justifyContent: 'center',
});
var RecipesListContent = styled.div((_a = {
        display: 'flex',
        width: '100%',
        maxWidth: '1200px',
        padding: '0 10px',
        boxSizing: 'border-box',
        flexWrap: 'wrap',
        justifyContent: 'space-around'
    },
    _a["&.".concat(EXTRA_LARGE_CLASS)] = {
        padding: 0,
    },
    _a));
var RecipeCardContainer = styled.div((_b = {
        width: '100%',
        marginTop: '30px',
        ':first-child': {
            marginTop: 0,
        }
    },
    _b["&.".concat(MEDIUM_CLASS)] = {
        width: '45%',
        ':nth-child(2)': {
            marginTop: 0, // reset blank margin-top rule from smaller viewport
        },
        ':nth-child(n + 3)': {
            marginTop: '30px',
        },
    },
    _b["&.".concat(LARGE_CLASS)] = {
        width: '30%',
        ':nth-child(2)': {
            marginTop: 0, // reset blank margin-top rule from smaller viewport
        },
        ':nth-child(3)': {
            marginTop: 0, // reset blank margin-top rule from smaller viewport
        },
        ':nth-child(n + 4)': {
            marginTop: '30px',
        },
    },
    _b["&.".concat(EXTRA_LARGE_CLASS)] = {
        width: '290px',
        ':nth-child(4)': {
            marginTop: 0, // reset blank margin-top rule from smaller viewport
        },
        ':nth-child(n + 5)': {
            marginTop: '30px',
        },
    },
    _b));
var FooterContainer = styled.div((_c = {
        marginTop: '19px',
        textAlign: 'center',
        width: '100%'
    },
    _c["&.".concat(MEDIUM_CLASS)] = {
        marginTop: '78px',
    },
    _c["&.".concat(EXTRA_LARGE_CLASS)] = {
        marginTop: '100px',
    },
    _c));
var LoadMoreButton = styled.button((_d = {
        backgroundColor: '#fff',
        border: 'solid 1px #131415',
        height: '44px',
        borderRadius: '4px',
        fontFamily: 'var(--font-family-primary)',
        fontSize: '15px',
        lineHeight: 'normal',
        textAlign: 'center',
        letterSpacing: 'normal',
        color: '#343434',
        cursor: 'pointer',
        width: '100%'
    },
    _d["".concat(FooterContainer, " &")] = {
        maxWidth: '301px',
    },
    _d["&.".concat(EXTRA_LARGE_CLASS)] = (_e = {},
        _e["".concat(FooterContainer, " &")] = {
            maxWidth: '210px',
        },
        _e),
    _d[':hover'] = {
        color: '#fff',
        backgroundColor: '#131415',
    },
    _d));
var EndReachedMessage = styled.span({
    fontFamily: 'var(--font-family-primary)',
    fontSize: '15px',
    color: 'rgba(52, 52, 52, 0.5)',
    lineHeight: 'normal',
    letterSpacing: 'normal',
});
function _RecipesList(_a) {
    var recipes = _a.recipes, showLoadMoreButton = _a.showLoadMoreButton, onLoadMoreClick = _a.onLoadMoreClick, renderItem = _a.renderItem, loadMoreMessage = _a.loadMoreMessage, emptyRecipesListMessage = _a.emptyRecipesListMessage, isLoading = _a.isLoading, _b = _a.loadingCount, loadingCount = _b === void 0 ? 10 : _b;
    var _c = useElementWidth(), ref = _c.ref, width = _c.width;
    var widthClassNames = useClassNames(width);
    var classNames = useCallback(function (x) { return x + ' ' + widthClassNames; }, [widthClassNames]);
    var skeletonItems = useMemo(function () {
        var renderCardSkeleton = function (key) { return (_jsx(RecipeCardContainer, __assign({ className: classNames(RECIPES_LIST_CARD) }, { children: _jsx(RecipeCardSkeleton, {}, void 0) }), key)); };
        var items = [renderCardSkeleton(1)];
        if (loadingCount > 1 && width >= MEDIUM_SIZE) {
            items.push(renderCardSkeleton(2));
        }
        if (loadingCount > 2 && width >= LARGE_SIZE) {
            items.push(renderCardSkeleton(3));
        }
        if (loadingCount > 3 && width >= EXTRA_LARGE_SIZE) {
            items.push(renderCardSkeleton(4));
        }
        return items;
    }, [loadingCount, width, classNames]);
    return (_jsx(RecipesListContainer, __assign({ ref: ref, className: classNames(RECIPES_LIST) }, { children: _jsxs(RecipesListContent, __assign({ className: classNames(RECIPES_LIST_CONTENT) }, { children: [isLoading && _jsx(_Fragment, { children: skeletonItems }, void 0), !isLoading && (_jsxs(_Fragment, { children: [recipes.map(function (recipe) { return (_jsx(RecipeCardContainer, __assign({ className: classNames(RECIPES_LIST_CARD) }, { children: renderItem(recipe) }), recipe.id)); }), _jsxs(FooterContainer, __assign({ className: classNames(RECIPES_LIST_FOOTER) }, { children: [showLoadMoreButton && (_jsx(LoadMoreButton, __assign({ className: RECIPES_LIST_LOAD_MORE, onClick: onLoadMoreClick }, { children: loadMoreMessage }), void 0)), !showLoadMoreButton && recipes.length === 0 && (_jsx(EndReachedMessage, __assign({ className: RECIPES_LIST_END_REACHED }, { children: emptyRecipesListMessage }), void 0))] }), void 0)] }, void 0))] }), void 0) }), void 0));
}
// This is done to support generics
export var RecipesList = memo(_RecipesList);
